import {
  assessmentRequiresWording,
  assessmentValueForClauseResponse,
  clausesInSection,
  findClause,
  type ChecklistSection,
  type ClauseResponse,
  type FieldReport,
} from '@cer-autofill/shared';

/** A missing answer is a display row, never an invented compliant response. */
export interface ReportChecklistRow {
  section: ChecklistSection;
  clauseId: string;
  question?: string;
  libraryId?: string;
  response?: ClauseResponse;
}
const identity = (section: ChecklistSection, id: string) =>
  section === 'cer-pv' ? id.replace(/\s+/g, '') : id;

export function reportChecklistRows(
  draft: Pick<FieldReport, 'jobId' | 'checklist'>,
): Map<ChecklistSection, ReportChecklistRow[]> {
  const sections = new Set<ChecklistSection>();
  if (/^PVD/i.test(draft.jobId)) sections.add('cer-pv');
  for (const response of draft.checklist) sections.add(response.section);
  const groups = new Map<ChecklistSection, ReportChecklistRow[]>();
  for (const section of sections) {
    const saved = draft.checklist.filter((r) => r.section === section);
    const used = new Set<ClauseResponse>();
    const rows: ReportChecklistRow[] = [];
    if (section === 'cer-pv') {
      for (const clause of clausesInSection(section)) {
        const response = saved.find(
          (r) => identity(section, r.clauseId) === identity(section, clause.clauseId),
        );
        if (response) used.add(response);
        rows.push({
          section,
          clauseId: response?.clauseId ?? clause.clauseId,
          libraryId: clause.clauseId,
          question: clause.question,
          ...(response ? { response } : {}),
        });
      }
    }
    // Retain unknown/legacy responses and battery sections; never discard evidence.
    for (const response of saved) {
      if (used.has(response)) continue;
      const question = findClause(section, response.clauseId)?.question;
      rows.push({
        section,
        clauseId: response.clauseId,
        response,
        ...(question ? { question } : {}),
      });
    }
    groups.set(section, rows);
  }
  return groups;
}

/**
 * Point a photo's clause-evidence links at wherever it is now filed.
 *
 * Same rule as the field draft: remove the photo from every clause, then add
 * it to the clause named by `clause:…` when that response already exists.
 * Re-filing to a shot or to unfiled drops the checklist link. Untouched
 * responses keep their identity.
 */
export function relinkChecklistPhoto(
  checklist: readonly ClauseResponse[],
  photoId: string,
  promptKey: string,
): ClauseResponse[] {
  const targetClauseId = promptKey.startsWith('clause:')
    ? promptKey.slice('clause:'.length)
    : undefined;
  return checklist.map((response) => {
    const without = response.photoIds.filter((id) => id !== photoId);
    const shouldHave = targetClauseId !== undefined && response.clauseId === targetClauseId;
    const photoIds = shouldHave ? [...without, photoId] : without;
    const unchanged =
      photoIds.length === response.photoIds.length &&
      photoIds.every((id, index) => id === response.photoIds[index]);
    return unchanged ? response : { ...response, photoIds };
  });
}

/** Keep the open report draft aligned with a photo that was just re-filed. */
export function applyPhotoRefile(
  draft: FieldReport,
  photoId: string,
  promptKey: string,
  filename: string,
): FieldReport {
  return {
    ...draft,
    photos: draft.photos.map((photo) =>
      photo.id === photoId ? { ...photo, promptKey, filename } : photo,
    ),
    checklist: relinkChecklistPhoto(draft.checklist, photoId, promptKey),
  };
}

export function updateReportClause(draft: FieldReport, response: ClauseResponse): FieldReport {
  const exists = draft.checklist.some(
    (r) => r.section === response.section && r.clauseId === response.clauseId,
  );
  const checklist = exists
    ? draft.checklist.map((r) =>
        r.section === response.section && r.clauseId === response.clauseId ? response : r,
      )
    : [...draft.checklist, response];
  const next = { ...draft, checklist };
  return {
    ...next,
    checklist: [...reportChecklistRows(next).values()]
      .flat()
      .flatMap((row) => (row.response ? [row.response] : [])),
  };
}

/**
 * Findings and unanswered questions remain visible during QA.
 * Wording counts only when the assessment requires it, so leftover text on
 * Compliant, N/A, Not Sighted or N/V stays hidden. No resolved assessment
 * still counts wording, so an older non-compliance keeps showing.
 */
export function isRoutineChecklistResponse(response?: ClauseResponse): boolean {
  if (!response) return false;
  const assessment = assessmentValueForClauseResponse(response);
  const wordingCounts =
    Boolean(response.recommendation?.trim()) &&
    (assessment === undefined || assessmentRequiresWording(assessment));
  if (wordingCounts || response.inspectorNote?.trim()) return false;
  return ['compliant', 'not_cited', 'not_viewed', 'not_applicable'].includes(assessment ?? '');
}
